import { mainnet } from 'wagmi'
import { Buffer } from 'buffer'
import { normalize } from 'viem/ens'
import { getRandomBytesSync } from 'ethereum-cryptography/random'
import { formatUnits, parseUnits, getAddress, createPublicClient, http } from 'viem'

import { ErrorType, ErrorsList, Hash } from 'types/entities'

import { StoredAirdropList } from 'state/airdrop/types'

import { SupportedChainId } from 'constants/chains'
import { errors as error, errors } from 'constants/errors'
import { numbers, etherNumbers } from 'constants/variables'
import { ETH_ADDRESS, ZERO_ADDRESS } from 'constants/addresses'

function bytesToHex(bytes: Uint8Array) {
  return Buffer.from(bytes).toString('hex')
}

export function randomHex(byteSize: number) {
  const bytes = getRandomBytesSync(byteSize)
  return bytesToHex(bytes)
}

export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function isEther(address: string) {
  const _address = toChecksumAddress(address)
  return compareAddresses(_address, ZERO_ADDRESS) || compareAddresses(_address, ETH_ADDRESS)
}

export function toChecksumAddress(value: any) {
  try {
    return getAddress(value) as Hash
  } catch {
    return '' as Hash
  }
}

export function compareAddresses(firstAddr?: string, secondAddr?: string): boolean {
  try {
    return toChecksumAddress(firstAddr) === toChecksumAddress(secondAddr)
  } catch (e) {
    return false
  }
}

export function toWei(value: string, uintName?: number): bigint {
  return parseUnits(value, uintName || 18)
}

export function generateAddress(): Hash {
  return `0x${randomHex(20)}`
}

export function fromWei(balance: string, unitName = etherNumbers.WEI_DECIMALS) {
  return formatUnits(BigInt(balance), unitName)
}

const ETHERSCAN_PREFIXES: { [chainId in SupportedChainId]: string } = {
  [SupportedChainId.MAINNET]: '', //api.
  [SupportedChainId.GOERLI]: 'goerli.', //api-goerli.
}

export function getEtherscanLink(
  chainId: SupportedChainId,
  data: string,
  type: 'transaction' | 'token' | 'address' | 'block',
): string {
  const prefix = `https://${ETHERSCAN_PREFIXES[chainId]}etherscan.io`

  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`
    }
    case 'token': {
      return `${prefix}/token/${data}`
    }
    case 'block': {
      return `${prefix}/block/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`
    }
  }
}

export function bump(gas: bigint, percent: number) {
  return (gas * BigInt(numbers.ONE_HUNDRED + percent)) / BigInt(numbers.ONE_HUNDRED)
}

const IsEnsName = async (address: string) => {
  try {
    const ensName = normalize(address)

    if (!(ensName?.slice(-4) === '.eth')) {
      return
    }

    const client = createPublicClient({ chain: mainnet, transport: http() })

    return await client.getEnsAddress({ name: ensName })
  } catch {
    return
  }
}

export async function getAddressListFromEns(recipientsList: StoredAirdropList) {
  const errors: ErrorsList = []
  const recipientsListWithoutEns: StoredAirdropList = []

  for (let i = 0; i < recipientsList.length; i++) {
    const { addr, amount } = recipientsList[i]
    const lineLength = Object.values(recipientsList[i]).join('').length

    try {
      const ens = await IsEnsName(addr)

      const ensNameOrAddress = (ens || addr) as Hash

      if (!ensNameOrAddress) {
        throw new Error(error.validation.INVALID_ADDRESS)
      }

      recipientsListWithoutEns.push({ addr: ensNameOrAddress, amount })
    } catch (err) {
      let errorType: ErrorType
      if (err.message.includes('amount')) {
        errorType = ErrorType.AMOUNT
      } else {
        errorType = ErrorType.ADDRESS
      }

      errors.push({ line: i, errorMessage: err.message, lineLength, type: errorType })
    }
  }

  return { recipientsListWithoutEns, errors }
}

export function checkIsAddress(address: string | void | null) {
  const isValid = isAddress(address)

  if (!isValid) {
    throw new Error(errors.validation.INVALID_ADDRESS)
  }
  return Boolean(isValid)
}

export function generateAddresses(length: number, amount = '0.0001') {
  return Array.from({ length }, () => ({
    addr: generateAddress(),
    amount,
  }))
}

export function increaseForLondonFork(number: bigint) {
  return (number * BigInt(etherNumbers.MARKET_INCREASE)) / BigInt(1000)
}
